import { Box, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { DataCollectionsService } from 'services/data-collections.service';

const CONTEXT_NAME = 'DataCollector';

export interface IDataCollector {
  collection: IDataCollection;

  // allow/disallow low confidence shots
  readonly handleToggleLC: () => Promise<void>;

  // true after firing, false after (re)fetching shots
  waiting: boolean;
  readonly setWaiting: (value: boolean) => void;

  readonly updateCollection: (value: Partial<IDataCollection>) => void;
}

const DEFAULT: IDataCollector = {
  // just a placeholder
  collection: {
    _changed: '',
    _created: '',
    _id: '',
    ball_type: BallType.MLB,
    collectionID: '',
    complete: false,
    listID: '',
    machineID: '',
    pitches: [],
    type: DataCollectionType.BallTypeCalibration,
    user: '',
  },

  handleToggleLC: async () => console.error(`${CONTEXT_NAME}: not init`),

  waiting: false,
  setWaiting: () => console.error(`${CONTEXT_NAME}: not init`),

  updateCollection: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const DataCollectorContext = createContext(DEFAULT);

interface IProps {
  id: string;
  children: ReactNode;
  onToggleLowConfidence?: () => Promise<void>;
}

export const DataCollectorProvider: FC<IProps> = (props) => {
  const [waiting, setWaiting] = useState(DEFAULT.waiting);

  const [collection, setCollection] = useState(DEFAULT.collection);

  useEffect(() => {
    if (!props.id) {
      return;
    }

    DataCollectionsService.getOne(props.id)
      .then((result) => setCollection(result))
      .catch((error) => {
        NotifyHelper.error({
          message_md: `Encountered an error while loading data collection \`${props.id}\`.`,
        });

        console.error(error);
      });
  }, [props.id]);

  const [dialogWarn, setDialogWarn] = useState<number>();

  const updateCollection = useCallback(
    async (value: Partial<IDataCollection>) => {
      if (!collection._id) {
        console.warn('Collection update skipped (empty _id)');
        return;
      }

      const result = await DataCollectionsService.updateOne(
        collection._id,
        value
      );

      setCollection(result);
    },
    [collection._id]
  );

  const state: IDataCollector = {
    collection: collection,

    handleToggleLC: async () => {
      if (!collection._id) {
        return;
      }

      // enabled => disabled
      if (collection.include_low_confidence) {
        await updateCollection({ include_low_confidence: false });
        await props.onToggleLowConfidence?.();
        return;
      }

      // disabled => show warning before enabling
      setDialogWarn(Date.now());
    },

    waiting: waiting,
    setWaiting: setWaiting,

    updateCollection: updateCollection,
  };

  return (
    <DataCollectorContext.Provider value={state}>
      {props.children}

      {dialogWarn && (
        <CommonConfirmationDialog
          key={dialogWarn}
          identifier="ConfirmLowConfidence"
          title="Allow Low Confidence Shots"
          content={
            <Flex direction="column" gap="4">
              <Box>
                Enabling this setting can significantly impact the accuracy and
                reliability of the data you're collecting.
              </Box>

              <Box>
                If you're unsure whether you should use this feature, please
                contact your Trajekt representative before proceeding.
              </Box>

              <CommonCallout text="Allowing low confidence shots is only recommended if a Trajekt representative has specifically instructed you to do so." />
            </Flex>
          }
          action={{
            label: 'Allow Low Confidence Shots',
            onClick: async () => {
              if (!collection._id) {
                return;
              }

              const result = await DataCollectionsService.updateOne(
                collection._id,
                { include_low_confidence: true }
              );

              setCollection(result);
              await props.onToggleLowConfidence?.();
            },
          }}
        />
      )}
    </DataCollectorContext.Provider>
  );
};
